import { Draggable } from '@hello-pangea/dnd'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers'
import moment, { Moment } from 'moment'
import React from 'react'

import { CampaignAddButton } from '@/app/module/campaigns/component/helpers'
import ChoiceLabel from '@/app/module/campaigns/component/item/steps/content/question/choice/choice-label'
import {
  ContentFlowDatePicker,
  ContentFlowElement,
  ContentFlowMultiSelect,
  ContentFlowSingleSelect,
  ContentFlowText,
} from '@/app/module/campaigns/types/common'
import { createCompactId } from '@/app/module/campaigns/utils/actions/ids'

import { useWaFlowFieldError } from './hooks/use-wa-flow-field-error'
import {
  CHOICE_MAX,
  DATE_PICKER_QUESTION_MAX_LENGTH,
  getElementTypeLabel,
  getWaFlowDateErrors,
  getWaFlowLengthError,
  HEADING_TEXT_MAX_LENGTH,
  LONG_TEXT_HELP_TEXT_MAX_LENGTH,
  LONG_TEXT_QUESTION_MAX_LENGTH,
  SELECT_ANSWER_MAX_LENGTH,
  SELECT_QUESTION_MAX_LENGTH,
  SHORT_TEXT_HELP_TEXT_MAX_LENGTH,
  SHORT_TEXT_QUESTION_MAX_LENGTH,
  SUBHEADING_TEXT_MAX_LENGTH,
} from './wa-flow-editor-utils'

const DATE_PICKER_FORMAT = 'YYYY-MM-DD'

const parseDatePickerValue = (value?: string) => {
  if (!value) {
    return null
  }

  const parsedDate = moment(value, DATE_PICKER_FORMAT, true)

  return parsedDate.isValid() ? parsedDate : null
}

const normalizeDatePickerInput = (inputValue: string): string | undefined => {
  const trimmedValue = inputValue.trim()

  if (!trimmedValue) {
    return undefined
  }

  const parsedDate = moment(trimmedValue, DATE_PICKER_FORMAT, true)

  return parsedDate.isValid() ? parsedDate.format(DATE_PICKER_FORMAT) : undefined
}

type Props = {
  element: ContentFlowElement
  headingLabel: string
  index: number
  isNewElement?: boolean
  pageId: string
  showErrorsOnMount: boolean

  onDatePickerChange: (elementId: string, value: ContentFlowDatePicker) => void
  onElementRemove: (elementId: string) => void
  onFocus?: (elementId: string) => void
  onHeadingChange: (elementId: string, key: 'heading' | 'subheading', value: string) => void
  onSelectChange: (
    elementId: string,
    key: 'singleSelect' | 'multiSelect',
    value: ContentFlowSingleSelect | ContentFlowMultiSelect,
  ) => void
  onTextChange: (elementId: string, key: 'shortText' | 'longText', value: ContentFlowText) => void
}

const WaFlowEditorElement: React.FC<Props> = ({
  element,
  headingLabel,
  index,
  isNewElement,
  pageId,
  showErrorsOnMount,
  onDatePickerChange,
  onElementRemove,
  onFocus,
  onHeadingChange,
  onSelectChange,
  onTextChange,
}) => {
  const elementTypeLabel = getElementTypeLabel(element)
  const elementId = element.questionId
  const handleFocus = React.useCallback(() => {
    onFocus?.(elementId)
  }, [elementId, onFocus])

  return (
    <Draggable draggableId={`${pageId}-${elementId}`} index={index}>
      {(draggableProvided, draggableSnapshot) => (
        <ElementCard
          $isDragging={draggableSnapshot.isDragging}
          elevation={0}
          ref={draggableProvided.innerRef}
          {...draggableProvided.draggableProps}
        >
          <Stack spacing={1.5}>
            <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <DragHandle
                  $isDragging={draggableSnapshot.isDragging}
                  {...draggableProvided.dragHandleProps}
                  data-testid={`wa-flow-element-drag-${index}`}
                >
                  <Icon color="action" fontSize="small">
                    drag_indicator
                  </Icon>
                </DragHandle>
                <Typography variant="subtitle2">{elementTypeLabel}</Typography>
              </Stack>
              <Stack alignItems="center" direction="row" spacing={1}>
                <RemoveButton
                  data-testid={`wa-flow-element-remove-${index}`}
                  size="small"
                  onClick={() => onElementRemove(elementId)}
                >
                  <Icon fontSize="small">close</Icon>
                </RemoveButton>
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <ElementEditor
                autoFocus={isNewElement}
                element={element}
                headingLabel={headingLabel}
                showErrorsOnMount={showErrorsOnMount}
                onDatePickerChange={onDatePickerChange}
                onFocus={handleFocus}
                onHeadingChange={onHeadingChange}
                onSelectChange={onSelectChange}
                onTextChange={onTextChange}
              />
            </Stack>
          </Stack>
        </ElementCard>
      )}
    </Draggable>
  )
}

type TextEditorProps = {
  autoFocus?: boolean
  elementId: string
  helpTextMaxLength: number
  questionMaxLength: number
  value: ContentFlowText
  showErrorsOnMount: boolean

  onChange: (elementId: string, value: ContentFlowText) => void
  onFocus: () => void
}

const TextEditor: React.FC<TextEditorProps> = ({
  autoFocus,
  elementId,
  helpTextMaxLength,
  questionMaxLength,
  showErrorsOnMount,
  value,

  onChange,
  onFocus,
}) => {
  const [helpTextError, revealHelpTextError] = useWaFlowFieldError(value.helpText, helpTextMaxLength, showErrorsOnMount)
  const [questionError, revealQuestionError] = useWaFlowFieldError(value.question, questionMaxLength, showErrorsOnMount)
  const questionInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!autoFocus) {
      return
    }

    const focusTimer = setTimeout(() => {
      questionInputRef.current?.focus()
      onFocus()
    }, 0)

    return () => clearTimeout(focusTimer)
  }, [autoFocus, onFocus])

  return (
    <>
      <TextField
        fullWidth
        error={!!questionError}
        helperText={questionError || undefined}
        inputRef={questionInputRef}
        label="Question"
        required
        value={value.question}
        variant="standard"
        onBlur={revealQuestionError}
        onChange={(event) =>
          onChange(elementId, {
            ...value,
            question: event.target.value,
          })
        }
      />
      <TextField
        fullWidth
        error={!!helpTextError}
        helperText={helpTextError || undefined}
        label="Help text"
        required
        value={value.helpText}
        variant="standard"
        onBlur={revealHelpTextError}
        onChange={(event) =>
          onChange(elementId, {
            ...value,
            helpText: event.target.value,
          })
        }
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={value.required}
            size="small"
            onChange={(event) =>
              onChange(elementId, {
                ...value,
                required: event.target.checked,
              })
            }
          />
        }
        label="Required"
        sx={{ mx: 0 }}
      />
    </>
  )
}

type SelectEditorProps = {
  autoFocus?: boolean
  elementId: string
  value: ContentFlowSingleSelect | ContentFlowMultiSelect
  showErrorsOnMount: boolean

  onChange: (elementId: string, value: ContentFlowSingleSelect | ContentFlowMultiSelect) => void
  onFocus: () => void
}

const SelectEditor: React.FC<SelectEditorProps> = ({
  autoFocus,
  elementId,
  showErrorsOnMount,
  value,
  onChange,
  onFocus,
}) => {
  const [newChoiceIds, setNewChoiceIds] = React.useState<Record<string, number>>({})
  const [visibleAnswerIds, setVisibleAnswerIds] = React.useState<string[]>(() =>
    showErrorsOnMount ? value.answers.map((answer) => answer.id) : [],
  )
  const answerErrors = Object.fromEntries(
    value.answers.map((answer) => [
      answer.id,
      visibleAnswerIds.includes(answer.id) ? getWaFlowLengthError(answer.value, SELECT_ANSWER_MAX_LENGTH) : '',
    ]),
  )
  const [questionError, revealQuestionError] = useWaFlowFieldError(
    value.question,
    SELECT_QUESTION_MAX_LENGTH,
    showErrorsOnMount,
  )
  const questionInputRef = React.useRef<HTMLInputElement>(null)

  const canAddChoice = value.answers.length < CHOICE_MAX
  React.useEffect(() => {
    if (!autoFocus) {
      return
    }

    const focusTimer = setTimeout(() => {
      questionInputRef.current?.focus()
      onFocus()
    }, 0)

    return () => clearTimeout(focusTimer)
  }, [autoFocus, onFocus])

  const handleAnswerAdd = React.useCallback(() => {
    if (!canAddChoice) {
      return
    }

    const choiceId = createCompactId()
    onChange(elementId, {
      ...value,
      answers: [
        ...value.answers,
        {
          id: choiceId,
          label: '',
          translations: {},
          value: '',
        },
      ],
    })
    setNewChoiceIds((s) => ({ ...s, [choiceId]: 1 }))
  }, [canAddChoice, elementId, onChange, value])

  const handleAnswerChange = React.useCallback(
    (answerId: string, key: 'label' | 'value', nextValue: string) => {
      onChange(elementId, {
        ...value,
        answers: value.answers.map((answer) => {
          if (answer.id !== answerId) {
            return answer
          }

          return {
            ...answer,
            [key]: nextValue,
          }
        }),
      })
    },
    [elementId, onChange, value],
  )

  const handleAnswerBlur = React.useCallback((answerId: string) => {
    setVisibleAnswerIds((ids) => (ids.includes(answerId) ? ids : [...ids, answerId]))
  }, [])

  const handleAnswerRemove = React.useCallback(
    (answerId: string) => {
      if (value.answers.length <= 1) {
        return
      }

      onChange(elementId, {
        ...value,
        answers: value.answers.filter((answer) => answer.id !== answerId),
      })
    },
    [elementId, onChange, value],
  )

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        error={!!questionError}
        helperText={questionError || undefined}
        inputRef={questionInputRef}
        label="Question"
        required
        variant="standard"
        onBlur={revealQuestionError}
        onChange={(event) =>
          onChange(elementId, {
            ...value,
            question: event.target.value,
          })
        }
        value={value.question}
      />
      <Stack spacing={1}>
        <Typography color="textSecondary" variant="caption">
          Choices
        </Typography>
        {value.answers.map((answer, index) => (
          <OptionCard key={answer.id} spacing={0.5}>
            <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1}>
              <Stack maxWidth={240}>
                <ChoiceLabel
                  label={answer.label}
                  onChange={(nextLabel) => {
                    handleAnswerChange(answer.id, 'label', nextLabel)
                  }}
                />
              </Stack>
              <SmallRemoveButton
                disabled={value.answers.length <= 1}
                size="small"
                onClick={() => handleAnswerRemove(answer.id)}
              >
                <Icon sx={{ fontSize: '1.25rem' }}>close</Icon>
              </SmallRemoveButton>
            </Stack>
            <TextField
              autoFocus={!!newChoiceIds[answer.id]}
              fullWidth
              error={!!answerErrors[answer.id]}
              helperText={answerErrors[answer.id] || undefined}
              label={`Choice ${index + 1}`}
              required
              variant="standard"
              onBlur={() => {
                handleAnswerBlur(answer.id)
                setNewChoiceIds((s) => {
                  const next = { ...s }
                  delete next[answer.id]
                  return next
                })
              }}
              onChange={(event) => {
                handleAnswerChange(answer.id, 'value', event.target.value)
              }}
              value={answer.value}
            />
          </OptionCard>
        ))}
      </Stack>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <FormControlLabel
          control={
            <Checkbox
              checked={value.required ?? false}
              size="small"
              onChange={(event) =>
                onChange(elementId, {
                  ...value,
                  required: event.target.checked,
                })
              }
            />
          }
          label="Required"
          sx={{ mx: 0 }}
        />
        <CampaignAddButton
          disabled={!canAddChoice}
          icon="add"
          size="small"
          text="Add choice"
          tooltip={!canAddChoice ? 'You have reached the maximum number of choices.' : ''}
          onClick={handleAnswerAdd}
        />
      </Stack>
    </Stack>
  )
}

const OptionCard = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1, 1.25),
}))

type ElementEditorProps = {
  autoFocus?: boolean
  element: ContentFlowElement
  headingLabel: string
  showErrorsOnMount: boolean

  onDatePickerChange: (elementId: string, value: ContentFlowDatePicker) => void
  onFocus: () => void
  onHeadingChange: (elementId: string, key: 'heading' | 'subheading', value: string) => void
  onSelectChange: (
    elementId: string,
    key: 'singleSelect' | 'multiSelect',
    value: ContentFlowSingleSelect | ContentFlowMultiSelect,
  ) => void
  onTextChange: (elementId: string, key: 'shortText' | 'longText', value: ContentFlowText) => void
}

type DatePickerFieldProps = {
  helperText?: string
  label: string
  showError: boolean
  value: Moment | null
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange: (nextValue: Moment | null) => void
}

const defaultDateTextFieldProps = {
  fullWidth: true,
  required: true,
  variant: 'standard' as const,
  slotProps: {
    inputLabel: {
      shrink: true,
    },
  },
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({ helperText, label, showError, value, onBlur, onChange }) => {
  const slotProps = React.useMemo(
    () => ({
      textField: {
        ...defaultDateTextFieldProps,
        error: showError,
        helperText,
        onBlur,
      },
    }),
    [helperText, showError, onBlur],
  )

  return (
    <DatePicker format={DATE_PICKER_FORMAT} label={label} value={value} onChange={onChange} slotProps={slotProps} />
  )
}

type DatePickerEditorProps = {
  autoFocus?: boolean
  elementId: string
  value: ContentFlowDatePicker
  showErrorsOnMount: boolean

  onChange: (elementId: string, value: ContentFlowDatePicker) => void
  onFocus: () => void
}

const DatePickerEditor: React.FC<DatePickerEditorProps> = ({
  autoFocus,
  elementId,
  showErrorsOnMount,
  value,
  onChange,
  onFocus,
}) => {
  const [questionError, revealQuestionError] = useWaFlowFieldError(
    value.question,
    DATE_PICKER_QUESTION_MAX_LENGTH,
    showErrorsOnMount,
  )
  const [unavailableDatesVisible, setUnavailableDatesVisible] = React.useState(showErrorsOnMount)
  const [minDateVisible, setMinDateVisible] = React.useState(showErrorsOnMount)
  const [maxDateVisible, setMaxDateVisible] = React.useState(showErrorsOnMount)
  const unavailableDatesInput = (value.unavailableDates || []).join(',')
  const questionInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!autoFocus) {
      return
    }

    const focusTimer = setTimeout(() => {
      questionInputRef.current?.focus()
      onFocus()
    }, 0)

    return () => clearTimeout(focusTimer)
  }, [autoFocus, onFocus])

  const handleUnavailableDatesBlur = React.useCallback(() => {
    setUnavailableDatesVisible(true)
    const unavailableDates = (value.unavailableDates || []).map((date) => date.trim()).filter(Boolean)
    onChange(elementId, {
      ...value,
      unavailableDates: unavailableDates.length ? unavailableDates : undefined,
    })
  }, [elementId, value, onChange])

  const handleMinDateBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setMinDateVisible(true)
      const nextMinDate = normalizeDatePickerInput(event.target.value)

      if (nextMinDate === value.minDate) {
        return
      }

      onChange(elementId, {
        ...value,
        minDate: nextMinDate,
      })
    },
    [elementId, onChange, value],
  )

  const handleMaxDateBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setMaxDateVisible(true)
      const nextMaxDate = normalizeDatePickerInput(event.target.value)

      if (nextMaxDate === value.maxDate) {
        return
      }

      onChange(elementId, {
        ...value,
        maxDate: nextMaxDate,
      })
    },
    [elementId, onChange, value],
  )

  const maxDateValue = React.useMemo(() => parseDatePickerValue(value.maxDate), [value.maxDate])
  const minDateValue = React.useMemo(() => parseDatePickerValue(value.minDate), [value.minDate])
  const dateErrors = getWaFlowDateErrors(value)
  const unavailableDatesError = unavailableDatesVisible ? dateErrors.unavailableDates : ''

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        error={!!questionError}
        helperText={questionError || undefined}
        inputRef={questionInputRef}
        label="Question"
        required
        value={value.question}
        variant="standard"
        onBlur={revealQuestionError}
        onChange={(event) =>
          onChange(elementId, {
            ...value,
            question: event.target.value,
          })
        }
      />
      <DatePickerField
        helperText={minDateVisible ? dateErrors.minDate || undefined : undefined}
        label="Earliest date"
        showError={minDateVisible && !!dateErrors.minDate}
        value={minDateValue}
        onBlur={handleMinDateBlur}
        onChange={(nextValue) =>
          onChange(elementId, {
            ...value,
            minDate: nextValue?.isValid() ? nextValue.format(DATE_PICKER_FORMAT) : undefined,
          })
        }
      />
      <DatePickerField
        helperText={maxDateVisible ? dateErrors.maxDate || undefined : undefined}
        label="Latest date"
        showError={maxDateVisible && !!dateErrors.maxDate}
        value={maxDateValue}
        onBlur={handleMaxDateBlur}
        onChange={(nextValue) =>
          onChange(elementId, {
            ...value,
            maxDate: nextValue?.isValid() ? nextValue.format(DATE_PICKER_FORMAT) : undefined,
          })
        }
      />
      <TextField
        error={!!unavailableDatesError}
        fullWidth
        helperText={unavailableDatesError || undefined}
        label="Unavailable dates"
        placeholder="YYYY-MM-DD, YYYY-MM-DD"
        value={unavailableDatesInput}
        variant="standard"
        onBlur={handleUnavailableDatesBlur}
        onChange={(event) => {
          onChange(elementId, {
            ...value,
            unavailableDates: event.target.value ? event.target.value.split(',') : undefined,
          })
        }}
      />
    </Stack>
  )
}

const ElementEditor: React.FC<ElementEditorProps> = ({
  autoFocus,
  element,
  headingLabel,
  showErrorsOnMount,

  onDatePickerChange,
  onFocus,
  onHeadingChange,
  onSelectChange,
  onTextChange,
}) => {
  const [headingError, revealHeadingError] = useWaFlowFieldError(
    element.heading?.text || '',
    HEADING_TEXT_MAX_LENGTH,
    showErrorsOnMount,
  )
  const [subheadingError, revealSubheadingError] = useWaFlowFieldError(
    element.subheading?.text || '',
    SUBHEADING_TEXT_MAX_LENGTH,
    showErrorsOnMount,
  )
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!autoFocus) {
      return
    }

    const focusTimer = setTimeout(() => {
      if (!inputRef.current) {
        return
      }
      inputRef.current.focus()
      onFocus()
    }, 0)

    return () => clearTimeout(focusTimer)
  }, [autoFocus, onFocus])

  const elementId = element.questionId

  switch (true) {
    case Boolean(element.heading): {
      return (
        <TextField
          fullWidth
          error={!!headingError}
          helperText={headingError || undefined}
          inputRef={inputRef}
          label={headingLabel}
          required
          variant="standard"
          onBlur={revealHeadingError}
          onChange={(event) => onHeadingChange(elementId, 'heading', event.target.value)}
          value={element.heading?.text || ''}
        />
      )
    }

    case Boolean(element.subheading): {
      return (
        <TextField
          fullWidth
          error={!!subheadingError}
          helperText={subheadingError || undefined}
          inputRef={inputRef}
          label="Subheading"
          required
          value={element.subheading?.text || ''}
          variant="standard"
          onBlur={revealSubheadingError}
          onChange={(event) => onHeadingChange(elementId, 'subheading', event.target.value)}
        />
      )
    }

    case Boolean(element.shortText):
      return (
        <TextEditor
          autoFocus={autoFocus}
          elementId={elementId}
          helpTextMaxLength={SHORT_TEXT_HELP_TEXT_MAX_LENGTH}
          questionMaxLength={SHORT_TEXT_QUESTION_MAX_LENGTH}
          showErrorsOnMount={showErrorsOnMount}
          value={
            element.shortText || {
              helpText: '',
              question: '',
              required: false,
            }
          }
          onChange={(elementId, value) => onTextChange(elementId, 'shortText', value)}
          onFocus={onFocus}
        />
      )

    case Boolean(element.longText):
      return (
        <TextEditor
          autoFocus={autoFocus}
          elementId={elementId}
          helpTextMaxLength={LONG_TEXT_HELP_TEXT_MAX_LENGTH}
          questionMaxLength={LONG_TEXT_QUESTION_MAX_LENGTH}
          showErrorsOnMount={showErrorsOnMount}
          value={
            element.longText || {
              helpText: '',
              question: '',
              required: false,
            }
          }
          onChange={(elementId, value) => onTextChange(elementId, 'longText', value)}
          onFocus={onFocus}
        />
      )

    case Boolean(element.singleSelect):
      return (
        <SelectEditor
          autoFocus={autoFocus}
          elementId={elementId}
          showErrorsOnMount={showErrorsOnMount}
          value={
            element.singleSelect || {
              answers: [
                {
                  id: createCompactId(),
                  label: '',
                  translations: {},
                  value: '',
                },
              ],
              question: '',
              required: false,
              translations: {},
            }
          }
          onChange={(elementId, value) => onSelectChange(elementId, 'singleSelect', value)}
          onFocus={onFocus}
        />
      )

    case Boolean(element.multiSelect):
      return (
        <SelectEditor
          autoFocus={autoFocus}
          elementId={elementId}
          showErrorsOnMount={showErrorsOnMount}
          value={
            element.multiSelect || {
              answers: [
                {
                  id: createCompactId(),
                  label: '',
                  translations: {},
                  value: '',
                },
              ],
              question: '',
              required: false,
              translations: {},
            }
          }
          onChange={(elementId, value) => onSelectChange(elementId, 'multiSelect', value)}
          onFocus={onFocus}
        />
      )

    case Boolean(element.datePicker):
      return (
        <DatePickerEditor
          autoFocus={autoFocus}
          elementId={elementId}
          showErrorsOnMount={showErrorsOnMount}
          value={
            element.datePicker || {
              question: '',
            }
          }
          onChange={onDatePickerChange}
          onFocus={onFocus}
        />
      )

    default:
      return <Typography color="textSecondary">This element type is read-only in this release.</Typography>
  }
}

const ElementCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== '$isDragging',
})<{ $isDragging: boolean }>(({ theme, $isDragging }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  ...($isDragging && {
    boxShadow: theme.shadows[2],
  }),
}))

const DragHandle = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isDragging',
})<{ $isDragging: boolean }>(({ $isDragging }) => ({
  alignItems: 'center',
  borderRadius: 0,
  cursor: $isDragging ? 'grabbing' : 'grab',
  display: 'flex',
  justifyContent: 'center',
  padding: '0 6px',
}))

const RemoveButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}))

const SmallRemoveButton = styled(RemoveButton)(({ theme }) => ({
  '.material-icons': {
    fontSize: '1.25rem',
  },
}))

export default WaFlowEditorElement

export type { Props as WaFlowEditorElementProps }
